import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Utensils } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // Import for button links

// Component Imports
import Hero from '../components/Hero';
import MissionVision from '../components/MissionVision';
import Founder from '../components/Founder';
import CorePillars from '../components/CorePillars';
import ImpactHighlight from '../components/ImpactHighlight';
import GetInvolved from '../components/GetInvolved';
import Services from '../components/Services';
import BackToSchool from '../components/BackToSchool';
import DailyFeeding from '../components/DailyFeeding';

export default function Home() {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-hidden bg-white">
      {/* 1. HERO SECTION */}
      <Hero />

      {/* 2. MISSION & VISION SLIDER */}
      <MissionVision />

      {/* 3. THE ISSUE & SOLUTION */}
      <section className="py-24 bg-gray-50 border-y border-gray-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
          
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-gh-orange font-black tracking-widest text-sm mb-4 uppercase">The Challenge</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-gh-blue mb-8 leading-tight">
              Giving Hands to those in need.
            </h3>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              Across our communities, countless children face the devastating reality of life without a home or family. 
              Orphans, the homeless, and children abandoned due to physical disabilities are often left without a voice.
            </p>
            <button 
              onClick={() => navigate('/sponsorship')}
              className="bg-gh-blue text-white px-8 py-3 rounded-full font-bold hover:bg-gh-blue/90 transition-all text-sm uppercase tracking-widest"
            >
              Learn More
            </button>
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-2 gap-4"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gh-blue p-8 rounded-3xl text-white mt-8 shadow-xl hover:rotate-2 transition-transform duration-300">
              <Heart className="mb-4 text-gh-orange" size={40} />
              <h4 className="text-xl font-bold mb-2 text-white">Loving Home</h4>
              <p className="text-blue-100 text-sm font-medium">A permanent sanctuary where every child belongs.</p>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-xl border-t-8 border-gh-orange hover:-rotate-2 transition-transform duration-300">
              <Utensils className="mb-4 text-gh-blue" size={40} />
              <h4 className="text-xl font-bold mb-2 text-gh-blue">Full Nutrition</h4>
              <p className="text-gray-500 text-sm font-medium">Healthy, warm meals served daily to nourish bodies.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. WHAT WE DO (Services) */}
      <Services />
      {/* 5. IMPACT STORY - BACK TO SCHOOL CAMPAIGN */}
      <BackToSchool />
      {/* 6 Project */}
      <DailyFeeding />
      {/* 7. FOUNDER SECTION */}
      <Founder />

      {/* 8. CORE PILLARS & IMPACT */}
      <CorePillars />
      <ImpactHighlight />

      {/* 9. CALL TO ACTION - Final Push */}
      <section className="py-24 px-6 bg-white">
        <motion.div 
          className="bg-gh-blue rounded-[3rem] p-12 md:p-20 text-center text-white relative overflow-hidden"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative z-10 max-w-3xl mx-auto">
            <h3 className="text-4xl md:text-5xl font-black mb-6 leading-tight">Ready to make a lasting impact?</h3>
            <p className="text-xl text-blue-100 mb-10 font-light">
              Whether through child sponsorship or partnership, your support changes lives.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button 
                onClick={() => navigate('/sponsorship')}
                className="bg-gh-orange text-white px-10 py-4 rounded-full font-bold hover:bg-orange-600 hover:scale-105 transition-all shadow-lg active:scale-95"
              >
                SPONSOR A CHILD
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="border-2 border-white px-10 py-4 rounded-full font-bold hover:bg-white hover:text-gh-blue transition-all active:scale-95"
              >
                GET IN TOUCH
              </button>
            </div>
          </div>
          <GetInvolved />

          <div className="absolute -top-24 -right-24 w-64 h-64 bg-gh-orange rounded-full opacity-10 blur-2xl"></div>
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-white rounded-full opacity-5 blur-2xl"></div>
        </motion.div>
      </section>
     
    </div>
  );
}